import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Plus, Trash, PencilSimple, Buildings, Tag, UserCircle, CaretDown, CaretUp,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ClientManagement = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [brandInput, setBrandInput] = useState('');

  // Form state
  const [form, setForm] = useState({
    legal_name: '', client_type: 'Parent Entity', gst_number: '', pan_number: '',
    address: '', business_category: '', brands: [],
  });
  const [contactPersons, setContactPersons] = useState([]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/clients`, { withCredentials: true });
      setClients(res.data);
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const resetForm = () => {
    setForm({ legal_name: '', client_type: 'Parent Entity', gst_number: '', pan_number: '', address: '', business_category: '', brands: [] });
    setContactPersons([]);
    setEditingClient(null);
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (client) => {
    setEditingClient(client);
    setForm({
      legal_name: client.legal_name, client_type: client.client_type,
      gst_number: client.gst_number, pan_number: client.pan_number,
      address: client.address, business_category: client.business_category,
      brands: [],
    });
    setContactPersons(client.contact_persons || []);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        contact_persons: contactPersons,
        brands: form.brands,
      };
      if (editingClient) {
        await axios.put(`${BACKEND_URL}/api/clients/${editingClient.id}`, {
          legal_name: form.legal_name, client_type: form.client_type,
          gst_number: form.gst_number, pan_number: form.pan_number,
          address: form.address, business_category: form.business_category,
          contact_persons: contactPersons,
        }, { withCredentials: true });
        toast.success('Client updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/clients`, payload, { withCredentials: true });
        toast.success('Client created');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Delete this client? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${clientId}`, { withCredentials: true });
      toast.success('Client deleted');
      fetchClients();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  const handleAddBrand = async (clientId) => {
    if (!brandInput.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/clients/${clientId}/brands`, { name: brandInput.trim() }, { withCredentials: true });
      toast.success(`Brand "${brandInput.trim()}" added`);
      setBrandInput('');
      fetchClients();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add brand'); }
  };

  const handleRemoveBrand = async (clientId, brandId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${clientId}/brands/${brandId}`, { withCredentials: true });
      toast.success('Brand removed');
      fetchClients();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to remove brand'); }
  };

  const addContactPerson = () => setContactPersons([...contactPersons, { name: '', phone: '', email: '', designation: '' }]);
  const updateContactPerson = (idx, field, value) => {
    const updated = [...contactPersons];
    updated[idx][field] = value;
    setContactPersons(updated);
  };
  const removeContactPerson = (idx) => setContactPersons(contactPersons.filter((_, i) => i !== idx));

  const addBrandToForm = () => {
    const val = brandInput.trim();
    if (val && !form.brands.includes(val)) {
      setForm({ ...form, brands: [...form.brands, val] });
      setBrandInput('');
    }
  };

  const isAdmin = user.role === 'admin';

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-600">Loading...</div></div>;
  }

  return (
    <div>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-800" data-testid="client-management-title">Client Master</h2>
            <p className="text-sm text-zinc-600 mt-1">{clients.length} clients registered</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="add-client-button">
                <Plus size={18} className="mr-1.5" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-zinc-900">
                  {editingClient ? 'Edit Client' : 'Register New Client'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Legal Name *</Label>
                    <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                      required className="mt-1 rounded-none" data-testid="client-legal-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Client Type</Label>
                    <select value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })}
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="client-type-select">
                      <option value="Parent Entity">Parent Entity</option>
                      <option value="Brand">Brand</option>
                      <option value="Individual">Individual</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">GST Number</Label>
                    <Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
                      className="mt-1 rounded-none" data-testid="client-gst" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">PAN Number</Label>
                    <Input value={form.pan_number} onChange={(e) => setForm({ ...form, pan_number: e.target.value })}
                      className="mt-1 rounded-none" data-testid="client-pan" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Address</Label>
                    <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                      rows={2} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="client-address" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Business Category</Label>
                    <Input value={form.business_category} onChange={(e) => setForm({ ...form, business_category: e.target.value })}
                      className="mt-1 rounded-none" placeholder="e.g., Retail, Hospitality" data-testid="client-category" />
                  </div>
                </div>

                {/* Brands (only for new client) */}
                {!editingClient && (
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Brands</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={brandInput} onChange={(e) => setBrandInput(e.target.value)}
                        placeholder="Brand name" className="rounded-none flex-1" data-testid="brand-name-input"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBrandToForm(); } }} />
                      <Button type="button" onClick={addBrandToForm} variant="outline" className="rounded-none border-zinc-300" data-testid="add-brand-form-btn">
                        <Plus size={16} />
                      </Button>
                    </div>
                    {form.brands.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.brands.map((b, i) => (
                          <span key={i} className="bg-blue-50 border border-blue-200 text-blue-700 text-xs px-2 py-1 flex items-center gap-1">
                            <Tag size={12} /> {b}
                            <button type="button" onClick={() => setForm({ ...form, brands: form.brands.filter((_, j) => j !== i) })}
                              className="text-blue-500 hover:text-red-600 ml-1">&times;</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Persons */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Contact Persons</Label>
                    <Button type="button" onClick={addContactPerson} variant="outline" size="sm" className="rounded-none border-zinc-300 h-7 text-xs" data-testid="add-contact-btn">
                      <Plus size={14} className="mr-1" /> Add Contact
                    </Button>
                  </div>
                  {contactPersons.map((cp, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-2 mt-2 items-end">
                      <Input placeholder="Name" value={cp.name} onChange={(e) => updateContactPerson(idx, 'name', e.target.value)} className="rounded-none text-sm" />
                      <Input placeholder="Phone" value={cp.phone} onChange={(e) => updateContactPerson(idx, 'phone', e.target.value)} className="rounded-none text-sm" />
                      <Input placeholder="Email" value={cp.email} onChange={(e) => updateContactPerson(idx, 'email', e.target.value)} className="rounded-none text-sm" />
                      <div className="flex gap-1">
                        <Input placeholder="Role" value={cp.designation} onChange={(e) => updateContactPerson(idx, 'designation', e.target.value)} className="rounded-none text-sm flex-1" />
                        <Button type="button" onClick={() => removeContactPerson(idx)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-9 px-2">
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="submit-client-button">
                  {editingClient ? 'Update Client' : 'Register Client'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {clients.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-12 text-center">
            <Buildings size={48} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-base font-medium text-zinc-600">No clients registered</p>
            <p className="text-sm text-zinc-500 mt-1">Add your first client to start linking projects</p>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((client) => {
              const isExpanded = expandedClient === client.id;
              return (
                <div key={client.id} className="bg-white border border-zinc-200" data-testid={`client-card-${client.id}`}>
                  {/* Client Header Row */}
                  <div className="p-5 flex items-start justify-between cursor-pointer" onClick={() => setExpandedClient(isExpanded ? null : client.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-mono bg-zinc-100 text-zinc-600 px-2 py-0.5 border border-zinc-200" data-testid={`client-code-${client.id}`}>{client.client_code}</span>
                        <h3 className="text-base font-semibold text-zinc-800" data-testid={`client-name-${client.id}`}>{client.legal_name}</h3>
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 border border-blue-200">{client.client_type}</span>
                        {client.ccf_bypassed && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 border border-amber-200">CCF Bypassed</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        {client.gst_number && <span>GST: {client.gst_number}</span>}
                        {client.business_category && <span>Category: {client.business_category}</span>}
                        <span>{client.brands?.length || 0} brands</span>
                        <span>{client.contact_persons?.length || 0} contacts</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={(e) => { e.stopPropagation(); openEdit(client); }} variant="outline" size="sm" className="rounded-none border-zinc-300 h-8 text-xs" data-testid={`edit-client-${client.id}`}>
                        <PencilSimple size={14} className="mr-1" /> Edit
                      </Button>
                      {isAdmin && (
                        <Button onClick={(e) => { e.stopPropagation(); handleDelete(client.id); }} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-8 text-xs" data-testid={`delete-client-${client.id}`}>
                          <Trash size={14} />
                        </Button>
                      )}
                      {isExpanded ? <CaretUp size={18} className="text-zinc-400" /> : <CaretDown size={18} className="text-zinc-400" />}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-zinc-200 p-5 bg-zinc-50 space-y-4">
                      {/* Brands Section */}
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><Tag size={14} /> Brands</h4>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {(client.brands || []).map((b) => (
                            <span key={b.id} className="bg-white border border-zinc-200 text-sm px-3 py-1.5 flex items-center gap-2" data-testid={`brand-${b.id}`}>
                              <Tag size={14} className="text-blue-600" /> {b.name}
                              {isAdmin && (
                                <button onClick={() => handleRemoveBrand(client.id, b.id)} className="text-zinc-400 hover:text-red-600 ml-1" data-testid={`remove-brand-${b.id}`}>&times;</button>
                              )}
                            </span>
                          ))}
                          {(!client.brands || client.brands.length === 0) && <span className="text-xs text-zinc-400">No brands added yet</span>}
                        </div>
                        <div className="flex gap-2 max-w-md">
                          <Input value={expandedClient === client.id ? brandInput : ''} onChange={(e) => setBrandInput(e.target.value)}
                            placeholder="Add brand name" className="rounded-none text-sm flex-1" data-testid={`add-brand-input-${client.id}`}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBrand(client.id); } }} />
                          <Button onClick={() => handleAddBrand(client.id)} variant="outline" className="rounded-none border-zinc-300" data-testid={`add-brand-btn-${client.id}`}>
                            <Plus size={16} className="mr-1" /> Add Brand
                          </Button>
                        </div>
                      </div>

                      {/* Contact Persons */}
                      {client.contact_persons && client.contact_persons.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><UserCircle size={14} /> Contact Persons</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {client.contact_persons.map((cp, idx) => (
                              <div key={idx} className="bg-white border border-zinc-200 p-3 text-sm">
                                <p className="font-semibold text-zinc-800">{cp.name || '-'}</p>
                                <p className="text-xs text-zinc-500 mt-0.5">{cp.designation || 'No role'}</p>
                                {cp.phone && <p className="text-xs text-zinc-600 mt-1">{cp.phone}</p>}
                                {cp.email && <p className="text-xs text-zinc-600">{cp.email}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Additional Info */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div><span className="font-bold uppercase tracking-wider text-zinc-500">PAN</span><p className="text-zinc-800 mt-0.5">{client.pan_number || '-'}</p></div>
                        <div><span className="font-bold uppercase tracking-wider text-zinc-500">Address</span><p className="text-zinc-800 mt-0.5">{client.address || '-'}</p></div>
                        <div><span className="font-bold uppercase tracking-wider text-zinc-500">CCF Status</span><p className="text-zinc-800 mt-0.5">{client.ccf_completed ? 'Completed' : client.ccf_bypassed ? 'Bypassed' : 'Pending'}</p></div>
                        <div><span className="font-bold uppercase tracking-wider text-zinc-500">Created</span><p className="text-zinc-800 mt-0.5">{new Date(client.created_at).toLocaleDateString()}</p></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientManagement;
